import React, { useState } from 'react';
import { Search, ExternalLink, Copy, CheckCircle } from 'lucide-react';

function GoogleReviewLinkFinder({ onLinkFound }) {
  const [businessName, setBusinessName] = useState('');
  const [location, setLocation] = useState('');
  const [foundLink, setFoundLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState(1);

  const handleSearch = () => {
    // Create a Google Maps search URL
    const searchQuery = encodeURIComponent(`${businessName} ${location}`);
    const searchUrl = `https://www.google.com/maps/search/${searchQuery}`;
    window.open(searchUrl, '_blank');
    setStep(2);
  };

  const handleLinkInput = (e) => {
    const link = e.target.value;
    setFoundLink(link);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(foundLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUseLink = () => {
    if (foundLink && onLinkFound) {
      onLinkFound(foundLink);
    }
  };

  const styles = {
    container: {
      backgroundColor: '#f8f9fa',
      padding: '20px',
      borderRadius: '8px',
      marginTop: '10px',
      border: '1px solid #dee2e6'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '15px'
    },
    title: {
      margin: 0,
      fontSize: '16px',
      fontWeight: '600',
      color: '#333'
    },
    stepIndicator: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px'
    },
    stepBadge: {
      padding: '5px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      backgroundColor: '#e9ecef',
      color: '#6c757d'
    },
    stepBadgeActive: {
      backgroundColor: '#007bff',
      color: 'white'
    },
    stepBadgeComplete: {
      backgroundColor: '#28a745',
      color: 'white'
    },
    inputGroup: {
      marginBottom: '15px'
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#555'
    },
    input: {
      width: '100%',
      padding: '10px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    button: {
      padding: '10px 20px',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    buttonSuccess: {
      backgroundColor: '#28a745'
    },
    buttonSecondary: {
      backgroundColor: '#6c757d'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px'
    },
    instructions: {
      backgroundColor: '#fff3cd',
      padding: '15px',
      borderRadius: '4px',
      marginBottom: '15px',
      fontSize: '14px',
      lineHeight: '1.6'
    },
    instructionsList: {
      marginTop: '10px',
      marginBottom: '0',
      paddingLeft: '20px'
    },
    linkPreview: {
      backgroundColor: 'white',
      padding: '10px',
      borderRadius: '4px',
      marginTop: '10px',
      fontSize: '12px',
      wordBreak: 'break-all',
      color: '#666',
      border: '1px solid #dee2e6'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <Search size={20} color="#007bff" />
        <h3 style={styles.title}>Google Review Link Finder</h3>
      </div>

      <div style={styles.stepIndicator}>
        <span style={{
          ...styles.stepBadge,
          ...(step === 1 ? styles.stepBadgeActive : step > 1 ? styles.stepBadgeComplete : {})
        }}>
          {step > 1 ? '✓' : '1'} Search
        </span>
        <span style={{
          ...styles.stepBadge,
          ...(step === 2 ? styles.stepBadgeActive : step > 2 ? styles.stepBadgeComplete : {})
        }}>
          {step > 2 ? '✓' : '2'} Find
        </span>
        <span style={{
          ...styles.stepBadge,
          ...(step === 3 ? styles.stepBadgeActive : {})
        }}>
          3 Use
        </span>
      </div>

      {step === 1 && (
        <>
          <div style={styles.instructions}>
            <strong>Step 1:</strong> Enter your business details to search Google Maps
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Business Name *</label>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g., The Wedding DJ"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Location (City/Area) *</label>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g., Cape Town, Western Cape"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          <button
            style={styles.button}
            onClick={handleSearch}
            disabled={!businessName || !location}
          >
            <Search size={16} />
            Search on Google Maps
          </button>
        </>
      )}

      {step === 2 && (
        <>
          <div style={styles.instructions}>
            <strong>Step 2:</strong> In the Google Maps window that just opened:
            <ol style={styles.instructionsList}>
              <li>Find your business in the search results</li>
              <li>Click on your business listing</li>
              <li>Look for the "Share" button</li>
              <li>Click "Share" and copy the link</li>
              <li>Paste the link below</li>
            </ol>
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#856404' }}>
              💡 <strong>Tip:</strong> The link should look like: https://maps.app.goo.gl/... or https://www.google.com/maps/place/...
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Paste your Google Maps link here *</label>
            <input
              style={styles.input}
              type="url"
              placeholder="https://maps.app.goo.gl/... or https://www.google.com/maps/place/..."
              value={foundLink}
              onChange={handleLinkInput}
            />
          </div>

          {foundLink && (
            <div style={styles.linkPreview}>
              Preview: {foundLink}
            </div>
          )}

          <div style={styles.buttonGroup}>
            <button
              style={styles.button}
              onClick={() => setStep(1)}
            >
              ← Back
            </button>
            <button
              style={{...styles.button, ...styles.buttonSuccess}}
              onClick={() => setStep(3)}
              disabled={!foundLink}
            >
              Continue →
            </button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div style={styles.instructions}>
            <strong>Step 3:</strong> Your Google Review link is ready!
          </div>

          <div style={styles.linkPreview}>
            {foundLink}
          </div>

          <div style={styles.buttonGroup}>
            <button
              style={styles.button}
              onClick={() => setStep(2)}
            >
              ← Back
            </button>
            <button
              style={{...styles.button, ...styles.buttonSecondary}}
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <CheckCircle size={16} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={16} />
                  Copy Link
                </>
              )}
            </button>
            <button
              style={{...styles.button, ...styles.buttonSuccess}}
              onClick={handleUseLink}
            >
              <CheckCircle size={16} />
              Use This Link
            </button>
          </div>

          <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
            <strong>Want to test it?</strong>{' '}
            <a
              href={foundLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#007bff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            >
              Open in new tab <ExternalLink size={12} />
            </a>
          </div>
        </>
      )}
    </div>
  );
}

export default GoogleReviewLinkFinder;